// AdminUI.jsx — Shared components for all content management pages
// Import from this file in any admin component:
// import { AdminShell, FilterBar, DeleteModal, SkeletonRows, EmptyState, PagePreview, IconEdit, IconDelete, IconPlus, IconEye, IconClose, IconChevron } from './AdminUI';

import React from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';

// ── Icons ─────────────────────────────────────────────────────────────────────
export const IconEdit    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
export const IconDelete  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
export const IconPlus    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
export const IconEye     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
export const IconClose   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
export const IconChevron = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>;
export const IconSave    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
export const IconBack    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
export const IconImage   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
export const IconEmpty   = () => <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>;
export const IconLink    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;

// ── Shared CSS injected once ──────────────────────────────────────────────────
let _cssInjected = false;
export const injectAdminCSS = () => {
  if (typeof document === 'undefined' || _cssInjected) return;
  _cssInjected = true;
  const s = document.createElement('style');
  s.id = 'admin-ui-styles';
  s.textContent = `
    @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:.5} }
    @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
    .admin-fade { animation: fadeIn 0.25s ease; }
    .admin-select { appearance:none; -webkit-appearance:none; }
    .admin-select-wrap { position:relative; }
    .admin-select-wrap > svg { position:absolute; right:11px; top:50%; transform:translateY(-50%); pointer-events:none; color:#9ca3af; }
    .admin-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; transition:opacity .15s,background .15s; border:none; text-decoration:none; }
    .admin-btn:hover { opacity:.88; }
    .admin-btn:disabled { opacity:.5; cursor:not-allowed; }
    .admin-btn-primary { background:#111827; color:#fff; }
    .admin-btn-danger  { background:#ef4444; color:#fff; }
    .admin-btn-ghost   { background:#fff; color:#374151; border:1px solid #e5e7eb; }
    .admin-btn-success { background:#16a34a; color:#fff; }
    .admin-row-action { display:flex; align-items:center; justify-content:center; gap:5px; padding:7px 12px; border-radius:6px; font-size:12px; font-weight:500; cursor:pointer; border:none; background:none; transition:background .15s; text-decoration:none; }
    .admin-row-action:hover { background:#f3f4f6; }
    .admin-row-action.edit  { color:#2563eb; } .admin-row-action.edit:hover  { background:#eff6ff; }
    .admin-row-action.del   { color:#ef4444; } .admin-row-action.del:hover   { background:#fef2f2; }
    .admin-row-action.view  { color:#6b7280; } .admin-row-action.view:hover  { background:#f9fafb; }
    .admin-table { width:100%; border-collapse:collapse; font-size:14px; }
    .admin-table th { background:#1f2937; color:#fff; font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:.5px; padding:12px 16px; text-align:left; }
    .admin-table td { padding:14px 16px; border-bottom:1px solid #f3f4f6; color:#374151; vertical-align:middle; }
    .admin-table tr:last-child td { border-bottom:none; }
    .admin-table tr:hover td { background:#f9fafb; }
    .admin-badge { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
    .sk { background:linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 50%,#f3f4f6 75%); background-size:200% 100%; animation:shimmer 1.2s infinite; border-radius:4px; }

    /* ── Form elements ───────────────────────────────────────────────── */
    .admin-field { margin-bottom:18px; }
    .admin-label { display:block; font-size:11px; font-weight:700; color:#6b7280; margin-bottom:6px; text-transform:uppercase; letter-spacing:.5px; }
    .admin-label .req { color:#ef4444; margin-left:3px; }
    .admin-input, .admin-textarea {
      width:100%; padding:10px 12px; font-size:13.5; color:#111827;
      border:1px solid #e5e7eb; border-radius:8px; background:#fff;
      outline:none; font-weight:500; box-sizing:border-box; transition:border-color .15s;
      font-family:inherit;
    }
    .admin-input:focus, .admin-textarea:focus { border-color:#111827; }
    .admin-input:disabled { background:#f9fafb; color:#9ca3af; cursor:not-allowed; }
    .admin-file-input { display:flex; align-items:center; gap:10px; border:2px dashed #e5e7eb; border-radius:8px; padding:14px 16px; cursor:pointer; background:#fafafa; transition:border-color .15s; }
    .admin-file-input:hover { border-color:#9ca3af; }
    .admin-file-input input { display:none; }
    .admin-card { background:#fff; border-radius:12px; border:1px solid #f0f0f0; box-shadow:0 1px 4px rgba(0,0,0,0.06); }
    .admin-card-header { padding:14px 20px; border-bottom:1px solid #f3f4f6; font-weight:700; font-size:14px; color:#111827; }
    .admin-card-body { padding:20px; }
    .admin-rte .ql-toolbar { border-radius:8px 8px 0 0; border-color:#e5e7eb; background:#f9fafb; }
    .admin-rte .ql-container { border-radius:0 0 8px 8px; border-color:#e5e7eb; min-height:120px; font-size:13.5px; }
  `;
  document.head.appendChild(s);
};

// ── Page shell ────────────────────────────────────────────────────────────────
export const AdminShell = ({ title, subtitle, actions, children, maxWidth = 2200 }) => {
  injectAdminCSS();
  return (
    <div style={{ display:'flex', background:'#f8fafc', minHeight:'100vh' }}>
      <Sidebar />
      <div style={{ flex:1, padding:'32px 28px', overflowX:'auto', maxWidth }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:21, fontWeight:800, color:'#111827', margin:0 }}>{title}</h1>
            {subtitle && <p style={{ margin:'4px 0 0', fontSize:13, color:'#6b7280' }}>{subtitle}</p>}
          </div>
          {actions && <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>{actions}</div>}
        </div>
        {children}
      </div>
    </div>
  );
};

// ── Filter bar ────────────────────────────────────────────────────────────────
export const FilterBar = ({ children, extra }) => (
  <div style={{
    background:'#fff', borderRadius:12, padding:'18px 20px',
    border:'1px solid #f0f0f0', marginBottom:20,
    display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(180px,1fr)) ${extra ? 'auto' : ''}`, gap:14, alignItems:'end',
  }}>
    {children}
    {extra && <div style={{ display:'flex', alignItems:'flex-end' }}>{extra}</div>}
  </div>
);

// ── Select field ──────────────────────────────────────────────────────────────
export const SelectField = ({ label, value, onChange, disabled, children }) => (
  <div>
    {label && <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#6b7280', marginBottom:5, textTransform:'uppercase', letterSpacing:.5 }}>{label}</label>}
    <div className="admin-select-wrap">
      <select value={value} onChange={onChange} disabled={disabled} className="admin-select" style={{
        width:'100%', padding:'9px 32px 9px 11px', fontSize:13.5, color:'#111827',
        border:'1px solid #e5e7eb', borderRadius:8, background: disabled ? '#f9fafb' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer', outline:'none', fontWeight:500,
        opacity: disabled ? .6 : 1,
      }}>
        {children}
      </select>
      <IconChevron />
    </div>
  </div>
);

// ── Delete confirmation modal ─────────────────────────────────────────────────
export const DeleteModal = ({ message = 'This action cannot be undone.', onConfirm, onCancel }) => (
  <div onClick={onCancel} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:32, maxWidth:400, width:'100%', boxShadow:'0 25px 60px rgba(0,0,0,0.25)' }}>
      <div style={{ width:50, height:50, borderRadius:'50%', background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
      </div>
      <h3 style={{ textAlign:'center', fontSize:17, fontWeight:700, color:'#111827', margin:'0 0 8px' }}>Confirm Delete</h3>
      <p style={{ textAlign:'center', color:'#6b7280', fontSize:14, margin:'0 0 24px' }}>{message}</p>
      <div style={{ display:'flex', gap:10 }}>
        <button onClick={onCancel}  className="admin-btn admin-btn-ghost" style={{ flex:1, justifyContent:'center' }}>Cancel</button>
        <button onClick={onConfirm} className="admin-btn admin-btn-danger" style={{ flex:1, justifyContent:'center' }}>Delete</button>
      </div>
    </div>
  </div>
);

// ── Skeleton table rows ───────────────────────────────────────────────────────
export const SkeletonRows = ({ cols = 5, rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: cols }).map((_, j) => (
          <td key={j} style={{ padding:'14px 16px', borderBottom:'1px solid #f3f4f6' }}>
            <div className="sk" style={{ height:14, width: j === 0 ? '40%' : j === cols-1 ? 60 : '80%' }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ── Empty state ───────────────────────────────────────────────────────────────
export const EmptyState = ({ message = 'No content yet', hint, action }) => (
  <tr>
    <td colSpan={99} style={{ padding:'52px 20px', textAlign:'center' }}>
      <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}><IconEmpty /></div>
      <p style={{ fontSize:15, fontWeight:700, color:'#374151', margin:'0 0 6px' }}>{message}</p>
      {hint && <p style={{ fontSize:13, color:'#9ca3af', margin:'0 0 20px' }}>{hint}</p>}
      {action}
    </td>
  </tr>
);

// ── Page iframe preview ───────────────────────────────────────────────────────
export const PagePreview = ({ url, show, onToggle }) => (
  <>
    {url && (
      <button onClick={onToggle} className="admin-btn admin-btn-ghost" style={{ display:'inline-flex' }}>
        <IconEye /> {show ? 'Hide' : 'Preview'} Page
      </button>
    )}
    {show && url && (
      <div className="admin-fade" style={{ marginTop:24, borderRadius:12, overflow:'hidden', border:'1px solid #e5e7eb' }}>
        <div style={{ background:'#f9fafb', padding:'10px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #e5e7eb' }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Live Page Preview</span>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#2563eb', textDecoration:'none', fontWeight:500 }}>Open in new tab →</a>
        </div>
        <iframe src={url} width="100%" height="540px" title="Page Preview" style={{ display:'block', border:'none' }} />
      </div>
    )}
  </>
);

// ── Section count badge ───────────────────────────────────────────────────────
export const CountBadge = ({ count, label = 'entries' }) => (
  <span className="admin-badge" style={{ background:'#f3f4f6', color:'#374151' }}>
    {count} {count === 1 ? label.replace(/s$/,'') : label}
  </span>
);

// ── Form components ─────────────────────────────────────────────────────────

// FormCard: white card wrapper for grouping form sections
export const FormCard = ({ title, children, style }) => {
  injectAdminCSS();
  return (
    <div className="admin-card" style={{ marginBottom:20, ...style }}>
      {title && <div className="admin-card-header">{title}</div>}
      <div className="admin-card-body">{children}</div>
    </div>
  );
};

// Field: label + content wrapper
export const Field = ({ label, required, hint, children }) => {
  injectAdminCSS();
  return (
    <div className="admin-field">
      {label && <label className="admin-label">{label}{required && <span className="req">*</span>}</label>}
      {children}
      {hint && <p style={{ margin:'5px 0 0', fontSize:12, color:'#9ca3af' }}>{hint}</p>}
    </div>
  );
};

// TextInput
export const TextInput = ({ value, onChange, type = 'text', placeholder, disabled, min }) => {
  injectAdminCSS();
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      className="admin-input"
    />
  );
};

// FileInput: styled dropzone-like file picker showing selected filename
export const FileInput = ({ onChange, accept, fileName, placeholder = 'Choose a file…' }) => {
  injectAdminCSS();
  return (
    <label className="admin-file-input">
      <IconImage />
      <span style={{ fontSize:13, color: fileName ? '#111827' : '#9ca3af', fontWeight: fileName ? 600 : 400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {fileName || placeholder}
      </span>
      <input type="file" accept={accept} onChange={onChange} />
    </label>
  );
};

// RichTextField: wraps RichTextEditor with admin styling
export const RichTextField = ({ value, onChange, RichTextEditor }) => {
  injectAdminCSS();
  return (
    <div className="admin-rte">
      <RichTextEditor value={value} onChange={onChange} />
    </div>
  );
};

// FormActions: sticky-ish footer action row
export const FormActions = ({ children }) => (
  <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
    {children}
  </div>
);